// Contacts, profil et règles de validation. Aucune donnée d’exemple ici, ni ailleurs.
import { info, isValidZone } from './time';
import { city, countryOf } from './places';

export type Role = 'client' | 'supplier';
export type SortKey = 'offset' | 'name' | 'availability';
export type Theme = 'light' | 'dark';

/** Horaires déclarés : minutes locales depuis minuit, jours travaillés (0 = dimanche). */
export interface Hours {
  tz: string;
  start: number;
  end: number;
  days: number[];
}

export interface Contact extends Hours {
  id: string;
  org: string;
  name: string;
  place: string;
  country: string;
  role: Role;
  color: string;
  favorite: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Profile extends Hours {
  place: string;
  country: string;
  displayName: string;
  replyEmail: string;
  theme: Theme;
  sort: SortKey;
}

export const COLORS: readonly string[] = ['#d9daf8', '#d1e8e0', '#f5dfcf', '#e6d9ef', '#d4e8f4', '#e8e8c8', '#f2d9e3', '#eadfcf'];

export const DEFAULT_PROFILE: Profile = {
  tz: 'Europe/Paris', place: 'Paris', country: 'France',
  start: 540, end: 1080, days: [1, 2, 3, 4, 5],
  displayName: '', replyEmail: '', theme: 'light', sort: 'offset',
};

export type Status = 'work' | 'awake' | 'night';

/** Au travail à cet instant ? Gère les horaires de nuit (début > fin : le jour coché est celui du début). */
export function working(h: Hours, ms: number): boolean {
  const p = info(h.tz, ms), m = p.minutes;
  if (h.start < h.end) return h.days.includes(p.dow) && m >= h.start && m < h.end;
  return (h.days.includes(p.dow) && m >= h.start) || (h.days.includes((p.dow + 6) % 7) && m < h.end);
}

export function status(h: Hours, ms: number): Status {
  if (working(h, ms)) return 'work';
  const p = info(h.tz, ms);
  return p.minutes < 420 || p.minutes >= 1320 ? 'night' : 'awake';
}

/** L’entreprise d’abord, la personne ensuite. */
export const label = (c: { org: string; name: string }): string => c.org || c.name;
export const sublabel = (c: { org: string; name: string }): string => (c.org ? c.name : '');
export const placeOf = (c: { place: string; tz: string }): string => c.place || city(c.tz);

export function initials(text: string): string {
  return text.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0] ?? '').join('').toUpperCase();
}

export function uid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID?.() ?? 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export class ValidationError extends Error {}

export const cleanText = (v: unknown, n: number): string => String(v ?? '').trim().slice(0, n);
export const cleanEmail = (v: unknown): string => {
  const s = cleanText(v, 120);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s : '';
};

function toInt(v: unknown, fallback: number): number {
  if (v === undefined || v === null || v === '') return fallback;
  return typeof v === 'number' ? v : Number(v);
}

export function validateHours(input: { tz?: unknown; start?: unknown; end?: unknown; days?: unknown }): Hours {
  if (!isValidZone(input.tz)) throw new ValidationError('Choisissez une ville dans la liste.');
  const start = toInt(input.start, 540), end = toInt(input.end, 1080);
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || start > 1439 || end < 0 || end > 1439 || start === end) {
    throw new ValidationError('Les horaires doivent être valides et différents.');
  }
  const days = input.days ?? [1, 2, 3, 4, 5];
  if (!Array.isArray(days) || !days.length || days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
    throw new ValidationError('Les jours travaillés sont invalides.');
  }
  return { tz: input.tz, start, end, days: [...new Set(days as number[])].sort((a, b) => a - b) };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Valide et normalise un contact venu de n’importe où (formulaire, import, serveur). Lève une erreur en français. */
export function validateContact(input: unknown, i = 0, now: () => string = () => new Date().toISOString()): Contact {
  if (!isRecord(input)) throw new ValidationError('Contact illisible.');
  const org = cleanText(input.org, 100), name = cleanText(input.name, 80);
  if (!org && !name) throw new ValidationError('Indiquez au moins une entreprise ou une personne.');
  const hours = validateHours(input);
  const color = typeof input.color === 'string' && /^#[0-9a-f]{6}$/i.test(input.color) ? input.color.toLowerCase() : COLORS[i % COLORS.length]!;
  const place = cleanText(input.place, 60);
  const country = cleanText(input.country, 60) || countryOf(hours.tz);
  const stamp = now();
  return {
    id: typeof input.id === 'string' && input.id ? input.id : uid(),
    org, name,
    ...hours,
    place, country,
    role: input.role === 'supplier' ? 'supplier' : 'client',
    color,
    favorite: input.favorite === true,
    createdAt: typeof input.createdAt === 'string' ? input.createdAt : stamp,
    updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : stamp,
    deletedAt: typeof input.deletedAt === 'string' ? input.deletedAt : null,
  };
}

export function validateProfile(input: unknown): Profile {
  if (!isRecord(input)) throw new ValidationError('Profil illisible.');
  const hours = validateHours({ tz: input.tz ?? DEFAULT_PROFILE.tz, start: input.start, end: input.end, days: input.days });
  return {
    ...hours,
    place: cleanText(input.place, 60) || city(hours.tz),
    country: cleanText(input.country, 60) || countryOf(hours.tz),
    displayName: cleanText(input.displayName, 60),
    replyEmail: cleanEmail(input.replyEmail),
    theme: input.theme === 'dark' ? 'dark' : 'light',
    sort: input.sort === 'name' || input.sort === 'availability' ? input.sort : 'offset',
  };
}
